import {
	CustomersIcon,
	DashboardIcon,
	ListIcon,
	LogsIcon,
	ShieldIcon,
	UserManageSolidIcon,
	WebhooksIcon,
	WrenchScrewdriverSolidIcon,
} from "@moznsystems/mozn-ui";
import type { ParseKeys } from "i18next";
import { FC, FunctionComponent, SVGProps } from "react";
import { CustomFieldsIcon } from "@/components/icons/custom-fields";
import { DeviceIcon } from "@/components/icons/device";
import { KeyIcon } from "@/components/icons/key";
import { SessionsIcon } from "@/components/icons/sessions";
import { useAuthStore } from "@/store/authStore";
import { FliptFlagType, RoleValues } from "@/types";
import { FLIPT_FLAGS, IS_SELF_HOSTED, ROLES } from "./constants";
import {
	ACCOUNT_SETTINGS_ROUTE,
	API_KEYS_ROUTE,
	CUSTOM_FIELDS_ROUTE,
	CUSTOMERS_ROUTE,
	DASHBOARD_ROUTE,
	DEVICES_ROUTE,
	EVENTS_ROUTE,
	LISTS_ROUTE,
	LOGS_ROUTE,
	RULES_ROUTE,
	SESSIONS_ROUTE,
	TEAM_MANAGEMENT_ROUTE,
	WEBHOOKS_ROUTE,
} from "./routes";

export type LayoutKey = ParseKeys<"layout">;

export const GROUPS = {
	MAIN: "nav.groups.main",
	CONFIGURATION: "nav.groups.configuration",
	ORGANIZATION: "nav.groups.organization",
	REPORTS: "nav.groups.reports",
	DEVELOPER: "nav.groups.developer",
} as const satisfies Record<string, LayoutKey>;

export type NavigationType = {
	group: LayoutKey;
	label: LayoutKey;
	flag?: keyof typeof FLIPT_FLAGS;
	url: string;
	routes?: string[];
	role?: RoleValues;
	hiddenSelfHosted?: boolean;
	icon:
		| FunctionComponent<
				SVGProps<SVGSVGElement> & {
					version?: "1" | "2";
				}
		  >
		| FC<SVGProps<SVGSVGElement>>;
	subItems: SubNavigationType[];
};

type SubNavigationType = {
	label: LayoutKey;
	url: string;
	role?: keyof typeof ROLES;
	flag?: keyof typeof FLIPT_FLAGS;
	hiddenSelfHosted?: boolean;
	routes: string[];
};

const navigation: NavigationType[] = [
	{
		group: GROUPS.MAIN,
		label: "nav.dashboard",
		url: DASHBOARD_ROUTE,
		hiddenSelfHosted: true,
		icon: DashboardIcon,
		subItems: [],
	},
	{
		group: GROUPS.MAIN,
		label: "nav.events",
		url: EVENTS_ROUTE,
		icon: ShieldIcon,
		subItems: [],
	},
	{
		group: GROUPS.MAIN,
		label: "nav.sessions",
		flag: "behavioral-biometrics-pages",
		url: SESSIONS_ROUTE,
		icon: SessionsIcon,
		subItems: [],
	},
	{
		group: GROUPS.MAIN,
		label: "nav.customers",
		url: CUSTOMERS_ROUTE,
		icon: CustomersIcon,
		subItems: [],
	},
	{
		group: GROUPS.MAIN,
		label: "nav.devices",
		url: DEVICES_ROUTE,
		icon: DeviceIcon,
		subItems: [],
	},
	{
		group: GROUPS.CONFIGURATION,
		label: "nav.rules",
		url: RULES_ROUTE,
		role: ROLES.ADMIN,
		icon: WrenchScrewdriverSolidIcon,
		subItems: [],
	},
	{
		group: GROUPS.CONFIGURATION,
		label: "nav.custom_fields",
		url: CUSTOM_FIELDS_ROUTE,
		role: ROLES.ADMIN,
		icon: CustomFieldsIcon,
		subItems: [],
	},
	{
		group: GROUPS.CONFIGURATION,
		label: "nav.lists",
		url: LISTS_ROUTE,
		icon: ListIcon,
		subItems: [],
	},
	{
		group: GROUPS.ORGANIZATION,
		label: "nav.account_settings",
		url: ACCOUNT_SETTINGS_ROUTE,
		icon: UserManageSolidIcon,
		subItems: [],
	},
	{
		group: GROUPS.ORGANIZATION,
		label: "nav.teams_members",
		url: TEAM_MANAGEMENT_ROUTE,
		role: ROLES.ADMIN,
		icon: CustomersIcon,
		subItems: [],
	},
	{
		group: GROUPS.REPORTS,
		label: "nav.logs",
		url: LOGS_ROUTE,
		icon: LogsIcon,
		subItems: [],
	},
	{
		group: GROUPS.DEVELOPER,
		label: "nav.api_keys",
		url: API_KEYS_ROUTE,
		role: ROLES.ADMIN,
		icon: KeyIcon,
		subItems: [],
	},
	{
		group: GROUPS.DEVELOPER,
		label: "nav.webhooks",
		flag: "webhooks-integration",
		url: WEBHOOKS_ROUTE,
		role: ROLES.ADMIN,
		hiddenSelfHosted: true,
		icon: WebhooksIcon,
		subItems: [],
	},
];

const canBeIncludedInNavList = (
	role: string,
	navItem: NavigationType | SubNavigationType,
	flags?: FliptFlagType,
) => {
	if (navItem.hiddenSelfHosted && IS_SELF_HOSTED) {
		return false;
	}

	if (navItem.flag && !flags?.[navItem.flag]) {
		return false;
	}

	if (navItem.role && role !== navItem.role) {
		return false;
	}

	return true;
};

const DEVICE_ONLY_URLS = [
	ACCOUNT_SETTINGS_ROUTE,
	TEAM_MANAGEMENT_ROUTE,
	LOGS_ROUTE,
	API_KEYS_ROUTE,
];

const isAllowedInDeviceFingerprintOnly = (
	url: string,
	deviceFingerprintOnly: boolean,
) => !deviceFingerprintOnly || DEVICE_ONLY_URLS.includes(url);

export const getNavigationItems = () => {
	const { user } = useAuthStore();

	const deviceFingerprintOnly =
		Array.isArray(user?.subscriptions) &&
		user!.subscriptions.length === 1 &&
		(user!.subscriptions as unknown as string[])[0] === "device-fingerprint";

	return navigation
		.filter(
			(navItem) =>
				canBeIncludedInNavList(user?.role, navItem, user?.flags) &&
				isAllowedInDeviceFingerprintOnly(navItem.url, deviceFingerprintOnly),
		)
		.map((navItem) => {
			if (navItem.subItems) {
				const filteredSubItems = navItem.subItems.filter(
					(subItem) =>
						canBeIncludedInNavList(user?.role, subItem, user?.flags) &&
						isAllowedInDeviceFingerprintOnly(
							subItem.url,
							deviceFingerprintOnly,
						),
				);
				navItem.subItems = filteredSubItems;
			}
			return navItem;
		});
};
